import type { FC } from "react";

import { Text } from "./Text";

export interface LoadingStateProps {
  /**
   * Type of loading state to display
   */
  variant?: "skeleton" | "spinner" | "pulse" | "robot";

  /**
   * Number of skeleton rows (for skeleton variant)
   */
  rows?: number;

  /**
   * Height of each skeleton row
   */
  rowHeight?: string;

  /**
   * Loading message to display
   */
  message?: string;

  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * LoadingSkeleton Component
 *
 * Displays animated skeleton placeholders while content is loading.
 */
export const LoadingSkeleton: FC<{
  rows?: number;
  rowHeight?: string;
  className?: string;
}> = ({ rows = 3, rowHeight = "h-10", className = "" }) => (
  <div className={`space-y-2 ${className}`.trim()}>
    {Array.from({ length: rows }).map((_, index) => (
      <div
        key={index}
        className={`animate-shimmer rounded-none border border-slate-300/50 bg-slate-100/60 dark:border-slate-700/50 dark:bg-white/[0.03] ${rowHeight}`.trim()}
        style={{ animationDelay: `${index * 100}ms` }}
      />
    ))}
  </div>
);

/**
 * LoadingSpinner Component
 *
 * Displays a circular loading spinner.
 */
export const LoadingSpinner: FC<{
  size?: "sm" | "md" | "lg";
  className?: string;
}> = ({ size = "md", className = "" }) => {
  const sizeClasses = {
    lg: "h-8 w-8 border-2",
    md: "h-6 w-6 border-2",
    sm: "h-3.5 w-3.5 border-2",
  };

  return (
    <div className="flex items-center justify-center">
      <div
        className={`animate-spin rounded-full border-slate-300 border-t-slate-500 dark:border-white/20 dark:border-t-white/70 ${sizeClasses[size]} ${className} `
          .trim()
          .replace(/\s+/g, " ")}
      />
    </div>
  );
};

/**
 * LoadingPulse Component
 *
 * Displays a pulsing overlay for loading states.
 */
export const LoadingPulse: FC<{ className?: string; message?: string }> = ({
  className = "",
  message = "Loading...",
}) => (
  <div className={`flex items-center justify-center py-8 ${className}`.trim()}>
    <div className="space-y-1.5">
      <div className="flex space-x-1.5">
        <div className="bg-arti-primary/70 h-2 w-2 animate-pulse rounded-full [animation-delay:0ms]" />
        <div className="bg-arti-primary/70 h-2 w-2 animate-pulse rounded-full [animation-delay:150ms]" />
        <div className="bg-arti-primary/70 h-2 w-2 animate-pulse rounded-full [animation-delay:300ms]" />
      </div>
      <Text as="p" className="text-center">
        {message}
      </Text>
    </div>
  </div>
);

/**
 * LoadingRobot Component
 *
 * Displays a terminal-style loading indicator with message.
 */
export const LoadingRobot: FC<{ className?: string; message?: string }> = ({
  className = "",
  message = "Arti is fetching your data...",
}) => (
  <div
    className={`flex flex-col items-center justify-center py-8 ${className}`.trim()}
    role="status"
    aria-live="polite"
    aria-label={message}
  >
    <div className="mb-4 flex items-center gap-1.5">
      <div className="animate-terminal-glyph-1 font-mono text-2xl font-bold text-emerald-500 [animation-delay:0ms]">
        ■
      </div>
      <div className="animate-terminal-glyph-2 font-mono text-2xl font-bold text-emerald-500 [animation-delay:200ms]">
        □
      </div>
      <div className="animate-terminal-glyph-3 font-mono text-2xl font-bold text-emerald-500 [animation-delay:400ms]">
        ·
      </div>
    </div>
    <Text as="p" className="text-center">
      {message}
    </Text>
  </div>
);

/**
 * LoadingState Component
 *
 * Main loading state component with multiple variants.
 *
 * @example
 * ```tsx
 * <LoadingState variant="skeleton" rows={5} />
 * <LoadingState variant="spinner" />
 * <LoadingState variant="pulse" />
 * ```
 */
export const LoadingState: FC<LoadingStateProps> = ({
  variant = "skeleton",
  rows = 3,
  rowHeight = "h-10",
  message,
  className = "",
}) => {
  switch (variant) {
    case "skeleton": {
      return (
        <LoadingSkeleton
          rows={rows}
          rowHeight={rowHeight}
          className={className}
        />
      );
    }
    case "spinner": {
      return <LoadingSpinner className={className} />;
    }
    case "pulse": {
      return <LoadingPulse message={message} className={className} />;
    }
    case "robot": {
      return <LoadingRobot message={message} className={className} />;
    }
    default: {
      return (
        <LoadingSkeleton
          rows={rows}
          rowHeight={rowHeight}
          className={className}
        />
      );
    }
  }
};

/**
 * TableSkeleton Component
 *
 * Specialized loading state for tables.
 */
export const TableSkeleton: FC<{
  rows?: number;
  columns?: number;
  className?: string;
}> = ({ rows = 5, columns = 4, className = "" }) => (
  <div
    className={`overflow-hidden border-2 border-slate-300 bg-white dark:border-slate-700 dark:bg-arti-background ${className}`.trim()}
  >
    {/* Header row */}
    <div className="flex border-b-2 border-slate-300 bg-slate-100 dark:border-slate-700 dark:bg-arti-panel">
      {Array.from({ length: columns }).map((_, index) => (
        <div key={`header-${index}`} className="flex-1 px-4 py-3">
          <div className="animate-shimmer h-3 w-3/4 bg-slate-200/80 dark:bg-white/10" />
        </div>
      ))}
    </div>
    {/* Data rows */}
    {Array.from({ length: rows }, (_, rowIndex) => rowIndex).map((rowIndex) => (
      <div
        key={rowIndex}
        className="flex border-b border-slate-200 last:border-b-0 dark:border-slate-700"
      >
        {Array.from({ length: columns }, (_, colIndex) => colIndex).map(
          (colIndex) => (
            <div
              key={`cell-${rowIndex}-${colIndex}`}
              className="flex-1 px-4 py-3"
            >
              <div
                className="animate-shimmer h-4 bg-slate-100/80 dark:bg-white/5"
                style={{
                  animationDelay: `${(rowIndex * columns + colIndex) * 50}ms`,
                  width: `${60 + ((rowIndex + colIndex) % 3) * 15}%`,
                }}
              />
            </div>
          ),
        )}
      </div>
    ))}
  </div>
);
